import type { StoreId } from "@KassaroAB/shared/types/ids";
import type {
  ChainStatsDB,
  ChainStatsStoreDB,
  StoreStatsDB,
  StoreStatsProductDB,
  StoreStatsGroupDB,
} from "@KassaroAB/shared/types/storeStats";
import { api } from "../utils/api";
import { statsPath, type StatsScope } from "../hooks/useStatsScope";

export type StatsPeriod = "days" | "weeks" | "months";
export type StatsOrderBy = "quantity" | "grossAmount";

export type ChainStatsRefreshResponse = {
  day: (ChainStatsDB & { id: string }) | null;
  week: (ChainStatsDB & { id: string }) | null;
  month: (ChainStatsDB & { id: string }) | null;
};

export const computeStoreStatsApi = async (
  storeId: StoreId
): Promise<unknown> => {
  return api.post(`stores/${storeId}/stats/compute`, {});
};

export const refreshChainStatsApi = async (
  chainId: string
): Promise<ChainStatsRefreshResponse> => {
  return api.post<ChainStatsRefreshResponse>(`chains/${chainId}/stats/refresh`, {});
};

const statsBase = (scope: StatsScope) => `${statsPath(scope)}/stats`;

export const getStatsApi = async (
  scope: StatsScope,
  period: StatsPeriod,
  from: string,
  to: string
): Promise<Array<StoreStatsDB & { id: string }>> => {
  const params = new URLSearchParams({ from, to });
  return api.get<Array<StoreStatsDB & { id: string }>>(
    `${statsBase(scope)}/${period}?${params.toString()}`
  );
};

export const getStoreStatsApi = async (
  storeId: StoreId,
  period: StatsPeriod,
  from: string,
  to: string
): Promise<Array<StoreStatsDB & { id: string }>> => {
  return getStatsApi({ type: "store", id: storeId }, period, from, to);
};

export const getStatsDayApi = async (
  scope: StatsScope,
  localDate: string
): Promise<StoreStatsDB | ChainStatsDB> => {
  return api.get<StoreStatsDB | ChainStatsDB>(
    `${statsBase(scope)}/days/${localDate}`
  );
};

export const getStoreStatsDayApi = async (
  storeId: StoreId,
  localDate: string
): Promise<StoreStatsDB> => {
  return getStatsDayApi({ type: "store", id: storeId }, localDate);
};

export const getStatsWeekApi = async (
  scope: StatsScope,
  weekId: string
): Promise<StoreStatsDB | ChainStatsDB> => {
  return api.get<StoreStatsDB | ChainStatsDB>(
    `${statsBase(scope)}/weeks/${weekId}`
  );
};

export const getStoreStatsWeekApi = async (
  storeId: StoreId,
  weekId: string
): Promise<StoreStatsDB> => {
  return getStatsWeekApi({ type: "store", id: storeId }, weekId);
};

export const getStatsMonthApi = async (
  scope: StatsScope,
  monthId: string
): Promise<StoreStatsDB | ChainStatsDB> => {
  return api.get<StoreStatsDB | ChainStatsDB>(
    `${statsBase(scope)}/months/${monthId}`
  );
};

export const getStoreStatsMonthApi = async (
  storeId: StoreId,
  monthId: string
): Promise<StoreStatsDB> => {
  return getStatsMonthApi({ type: "store", id: storeId }, monthId);
};

const buildItemQuery = (params: {
  limit?: number;
  cursor?: string;
  orderBy?: StatsOrderBy;
}): string => {
  const parts: string[] = [];
  if (params.limit !== undefined) parts.push(`limit=${params.limit}`);
  if (params.cursor) parts.push(`cursor=${encodeURIComponent(params.cursor)}`);
  if (params.orderBy) parts.push(`orderBy=${params.orderBy}`);
  return parts.join("&");
};

export const getStatsProductsApi = async (
  scope: StatsScope,
  period: StatsPeriod,
  periodId: string,
  params: {
    limit?: number;
    cursor?: string;
    orderBy?: StatsOrderBy;
    storeId?: string;
  } = {}
): Promise<StoreStatsProductDB[]> => {
  const query = buildItemQuery(params);
  const storeSegment =
    scope.type === "chain" && params.storeId
      ? `/stores/${params.storeId}`
      : "";
  const base = `${statsBase(scope)}/${period}/${periodId}${storeSegment}/products`;
  return api.get<StoreStatsProductDB[]>(query ? `${base}?${query}` : base);
};

export const getStoreStatsProductsApi = async (
  storeId: StoreId,
  period: StatsPeriod,
  periodId: string,
  params: { limit?: number; cursor?: string; orderBy?: StatsOrderBy } = {}
): Promise<StoreStatsProductDB[]> => {
  return getStatsProductsApi(
    { type: "store", id: storeId },
    period,
    periodId,
    params
  );
};

export const getStatsGroupsApi = async (
  scope: StatsScope,
  period: StatsPeriod,
  periodId: string,
  params: {
    limit?: number;
    cursor?: string;
    orderBy?: StatsOrderBy;
    storeId?: string;
  } = {}
): Promise<StoreStatsGroupDB[]> => {
  const query = buildItemQuery(params);
  const storeSegment =
    scope.type === "chain" && params.storeId
      ? `/stores/${params.storeId}`
      : "";
  const base = `${statsBase(scope)}/${period}/${periodId}${storeSegment}/groups`;
  return api.get<StoreStatsGroupDB[]>(query ? `${base}?${query}` : base);
};

export const getChainStatsStoresApi = async (
  chainId: string,
  period: StatsPeriod,
  periodId: string
): Promise<ChainStatsStoreDB[]> => {
  return api.get<ChainStatsStoreDB[]>(
    `chains/${chainId}/stats/${period}/${periodId}/stores`
  );
};

export const getStoreStatsGroupsApi = async (
  storeId: StoreId,
  period: StatsPeriod,
  periodId: string,
  params: { limit?: number; cursor?: string; orderBy?: StatsOrderBy } = {}
): Promise<StoreStatsGroupDB[]> => {
  return getStatsGroupsApi(
    { type: "store", id: storeId },
    period,
    periodId,
    params
  );
};
